'use client'

import {
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
  EmojiPicker as EmojiPickerPrimitive,
} from 'frimousse'
import type * as React from 'react'

import { faSearch, faSpinner } from '@document-crunch/pro-regular-svg-icons'

import { cn } from '@dc-shared-web/lib'
import { Icon } from '@dc-shared-web/components/base'

function EmojiPicker({
  className,
  ...props
}: React.ComponentProps<typeof EmojiPickerPrimitive.Root>) {
  return (
    <EmojiPickerPrimitive.Root
      className={cn(
        'tw-bg-popover tw-text-popover-foreground tw-isolate tw-flex tw-h-full tw-w-fit tw-flex-col tw-overflow-hidden tw-rounded-md',
        className
      )}
      data-slot='emoji-picker'
      {...props}
    />
  )
}

function EmojiPickerSearch({
  className,
  ...props
}: React.ComponentProps<typeof EmojiPickerPrimitive.Search>) {
  return (
    <div
      className={cn(
        'tw-flex tw-h-9 tw-items-center tw-gap-2 tw-border-b tw-p-2',
        className
      )}
      data-slot='emoji-picker-search-wrapper'
    >
      <Icon icon={faSearch} size='sm' />
      <EmojiPickerPrimitive.Search
        className='tw-outline-hidden placeholder:tw-text-muted-foreground tw-flex tw-w-full tw-rounded-md tw-bg-transparent tw-p-1 tw-text-sm disabled:tw-cursor-not-allowed disabled:tw-opacity-50 tw-outline-0 focus:tw-outline-0'
        data-slot='emoji-picker-search'
        placeholder='Search...'
        autoFocus
        {...props}
      />
    </div>
  )
}

function EmojiPickerRow({ children, ...props }: EmojiPickerListRowProps) {
  return (
    <div
      {...props}
      className='tw-scroll-my-1 tw-px-1'
      data-slot='emoji-picker-row'
    >
      {children}
    </div>
  )
}

function EmojiPickerEmoji({
  emoji,
  className,
  ...props
}: EmojiPickerListEmojiProps) {
  return (
    <button
      {...props}
      className={cn(
        'data-[active]:tw-bg-accent tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-sm tw-text-base',
        className
      )}
      data-slot='emoji-picker-emoji'
    >
      {emoji.emoji}
    </button>
  )
}

function EmojiPickerCategoryHeader({
  category,
  ...props
}: EmojiPickerListCategoryHeaderProps) {
  return (
    <div
      {...props}
      className='tw-bg-popover tw-text-muted-foreground tw-px-3 tw-pb-2 tw-pt-3.5 tw-text-xs tw-leading-none'
      data-slot='emoji-picker-category-header'
    >
      {category.label}
    </div>
  )
}

function EmojiPickerContent({
  className,
  ...props
}: React.ComponentProps<typeof EmojiPickerPrimitive.Viewport>) {
  return (
    <EmojiPickerPrimitive.Viewport
      className={cn('tw-outline-hidden tw-relative tw-flex-1', className)}
      data-slot='emoji-picker-viewport'
      {...props}
    >
      <EmojiPickerPrimitive.Loading
        className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-muted-foreground'
        data-slot='emoji-picker-loading'
      >
        <Icon icon={faSpinner} className='tw-size-4 tw-animate-spin' />
      </EmojiPickerPrimitive.Loading>
      <EmojiPickerPrimitive.Empty
        className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-muted-foreground tw-text-sm'
        data-slot='emoji-picker-empty'
      >
        No emoji found.
      </EmojiPickerPrimitive.Empty>
      <EmojiPickerPrimitive.List
        className='tw-select-none tw-pb-1'
        components={{
          Row: EmojiPickerRow,
          Emoji: EmojiPickerEmoji,
          CategoryHeader: EmojiPickerCategoryHeader,
        }}
        data-slot='emoji-picker-list'
      />
    </EmojiPickerPrimitive.Viewport>
  )
}

function EmojiPickerFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'tw-max-w-(--frimousse-viewport-width) tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-1 tw-border-t tw-p-2',
        className
      )}
      data-slot='emoji-picker-footer'
      {...props}
    >
      <EmojiPickerPrimitive.ActiveEmoji>
        {({ emoji }) =>
          emoji ? (
            <>
              <div className='tw-flex tw-size-7 tw-flex-none tw-items-center tw-justify-center tw-text-lg'>
                {emoji.emoji}
              </div>
              <span className='tw-text-secondary-foreground tw-truncate tw-text-xs'>
                {emoji.label}
              </span>
            </>
          ) : (
            <span className='tw-text-muted-foreground tw-ml-1.5 tw-flex tw-h-7 tw-items-center tw-truncate tw-text-xs'>
              Select an emoji…
            </span>
          )
        }
      </EmojiPickerPrimitive.ActiveEmoji>
    </div>
  )
}

export { EmojiPicker, EmojiPickerSearch, EmojiPickerContent, EmojiPickerFooter }
